import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDistanceToNow, parseISO } from "date-fns";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import {
  handleGetNotifications,
  handleGetUnreadNotificationCount,
  handleMarkAllNotificationsRead,
  handleMarkNotificationRead,
  selectNotifications,
} from "../../features/notifications/notificationsSlice";

export const NOTIFICATION_POLL_MS = 60000;

const timeAgo = (value) => (value ? formatDistanceToNow(parseISO(value), { addSuffix: true }) : "");

const NotificationBell = () => {
  const dispatch = useDispatch();
  const { status, items, unread } = useSelector(selectNotifications);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const refresh = () => dispatch(handleGetUnreadNotificationCount());
    refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, NOTIFICATION_POLL_MS);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!open) return undefined;
    dispatch(handleGetNotifications());
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open, dispatch]);

  const openNotification = (notification) => {
    if (!notification.readAt) dispatch(handleMarkNotificationRead(notification.id));
    if (notification.link) window.open(notification.link, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-theme-secondary hover:bg-surface-hover transition-colors"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-4 text-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="menu"
            aria-label="Notifications"
            className="absolute right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-surface-modal shadow-xl ring-1 ring-surface-border z-50"
          >
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-divider">
              <p className="text-sm font-semibold text-theme">Notifications</p>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => dispatch(handleMarkAllNotificationsRead())}
                  className="text-xs font-semibold text-primary hover:text-primary900"
                >
                  Mark all read
                </button>
              )}
            </div>
            {status === "loading" && items.length === 0 && (
              <p className="px-5 py-4 text-sm text-theme-secondary">Loading notifications...</p>
            )}
            {status === "failed" && items.length === 0 && (
              <div className="flex items-center justify-between gap-3 px-5 py-4">
                <p className="text-sm text-theme-secondary">Notifications unavailable</p>
                <button
                  type="button"
                  onClick={() => dispatch(handleGetNotifications())}
                  className="text-xs font-semibold text-primary hover:text-primary900"
                >
                  Retry
                </button>
              </div>
            )}
            {status === "succeeded" && items.length === 0 && (
              <p className="px-5 py-4 text-sm text-theme-secondary">No notifications</p>
            )}
            {items.length > 0 && (
              <ul className="max-h-96 overflow-y-auto divide-y divide-divider">
                {items.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => openNotification(notification)}
                      className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-surface-hover transition-colors"
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.readAt ? "bg-transparent" : "bg-primary"}`}
                      />
                      <span className="min-w-0">
                        <span className={`block text-sm leading-5 break-words ${notification.readAt ? "text-theme-secondary" : "font-semibold text-theme"}`}>
                          {notification.title}
                        </span>
                        {notification.content && (
                          <span className="mt-0.5 block text-xs text-theme-muted break-words">{notification.content}</span>
                        )}
                        <span className="mt-1 block text-[11px] text-theme-muted">{timeAgo(notification.triggerTime)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
